(() => {
  'use strict';

  /* =========================================================
     CLEVER FINDS — MASTER THEME JS
     Navigation / Cart / Quick Add / Mobile / Accessibility
     ========================================================= */

  document.documentElement.classList.remove('no-js');


  /* =========================================================
     HELPERS
     ========================================================= */

  const $ = (selector, scope = document) => {
    return scope.querySelector(selector);
  };

  const $$ = (selector, scope = document) => {
    return Array.from(scope.querySelectorAll(selector));
  };


  function escapeHTML(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }


  async function fetchJSON(url, options = {}) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      ...options
    });

    let data = null;

    try {
      data = await response.json();
    } catch (error) {
      data = null;
    }

    if (!response.ok) {
      const message =
        data?.description ||
        data?.message ||
        'Something went wrong.';

      throw new Error(message);
    }

    return data;
  }


  /* =========================================================
     PRODUCT TITLE TRANSLATIONS
     ========================================================= */

  const titleMap = {
    'Câmera Spy 360': 'Spy 360 Camera',
    'Jaqueta Militar Drymax': 'Drymax Military Jacket'
  };


  function displayCartTitle(title) {
    return titleMap[title] || title;
  }


  /* =========================================================
     NAVIGATION
     ========================================================= */

  function initNavigation() {

    const toggles = $$('[data-nav-toggle]');
    const nav = $('[data-nav]');

    if (!nav) {
      return;
    }


    function closeNav() {

      nav.classList.remove('is-open');

      document.body.classList.remove(
        'nav-is-open'
      );

      toggles.forEach(toggle => {
        toggle.setAttribute(
          'aria-expanded',
          'false'
        );
      });

    }


    function openNav() {

      nav.classList.add('is-open');

      document.body.classList.add(
        'nav-is-open'
      );

      toggles.forEach(toggle => {
        toggle.setAttribute(
          'aria-expanded',
          'true'
        );
      });

    }


    toggles.forEach(toggle => {

      toggle.setAttribute(
        'aria-expanded',
        nav.classList.contains('is-open')
          ? 'true'
          : 'false'
      );


      toggle.addEventListener(
        'click',
        event => {

          event.preventDefault();

          if (
            nav.classList.contains('is-open')
          ) {
            closeNav();
          } else {
            openNav();
          }

        }
      );

    });


    $$('a', nav).forEach(link => {

      link.addEventListener(
        'click',
        closeNav
      );

    });


    document.addEventListener(
      'keydown',
      event => {

        if (event.key !== 'Escape') {
          return;
        }

        closeNav();

      }
    );


    window.addEventListener(
      'resize',
      () => {

        if (window.innerWidth > 990) {
          closeNav();
        }

      }
    );

  }


  /* =========================================================
     CART ELEMENTS
     ========================================================= */

  const drawer = $('[data-cart-drawer]');
  const drawerBody = $('[data-cart-body]');

  const cartOpeners =
    $$('[data-cart-open]');

  const cartClosers =
    $$('[data-cart-close]');


  /* =========================================================
     CART COUNT
     ========================================================= */

  function updateCartCount(itemCount) {

    $$('[data-cart-count]')
      .forEach(element => {

        element.textContent =
          String(itemCount);

        const hasItems =
          itemCount > 0;

        element.classList.toggle(
          'has-items',
          hasItems
        );


        if ('hidden' in element) {
          element.hidden =
            !hasItems;
        }

      });

  }


  /* =========================================================
     MONEY
     ========================================================= */

  function formatMoney(cents, currency) {

    const locale =
      document.documentElement.lang ||
      'en-US';


    try {

      return new Intl.NumberFormat(
        locale,
        {
          style: 'currency',
          currency: currency || 'USD'
        }
      ).format(
        Number(cents || 0) / 100
      );

    } catch (error) {

      return `$${(
        Number(cents || 0) / 100
      ).toFixed(2)}`;

    }

  }


  /* =========================================================
     CART RENDER
     ========================================================= */

  let cartRequest = null;


  async function renderCart() {

    if (!drawerBody) {
      return null;
    }


    if (cartRequest) {
      return cartRequest;
    }


    cartRequest = (async () => {

      const cart =
        await fetchJSON('/cart.js');


      updateCartCount(
        cart.item_count
      );


      if (!cart.items?.length) {

        drawerBody.innerHTML = `
          <div class="empty-state">
            <strong>
              Your cart is empty.
            </strong>

            <p class="muted">
              Explore a clever find for your space.
            </p>

            <a
              href="/collections/all"
              class="btn btn--dark"
            >
              Explore products
            </a>
          </div>
        `;

        return cart;

      }


      const itemsHTML =
        cart.items
          .map(item => {

            const title =
              displayCartTitle(
                item.product_title
              );


            const variant =
              item.variant_title &&
              item.variant_title !== 'Default Title'
                ? item.variant_title
                : '';


            const imageHTML =
              item.image

                ? `
                  <a
                    href="${escapeHTML(item.url)}"
                    class="drawer-line__image"
                    aria-label="${escapeHTML(title)}"
                  >
                    <img
                      src="${escapeHTML(item.image)}"
                      alt="${escapeHTML(title)}"
                      loading="lazy"
                    >
                  </a>
                `

                : `
                  <div
                    class="drawer-line__placeholder"
                    aria-hidden="true"
                  >
                    ✦
                  </div>
                `;


            return `
              <div
                class="drawer-line"
                data-line-item="${escapeHTML(item.key)}"
              >

                ${imageHTML}

                <div class="drawer-line__info">

                  <a href="${escapeHTML(item.url)}">
                    <strong>
                      ${escapeHTML(title)}
                    </strong>
                  </a>

                  ${
                    variant
                      ? `
                        <div class="muted">
                          ${escapeHTML(variant)}
                        </div>
                      `
                      : ''
                  }

                  <div class="drawer-line__price">
                    ${escapeHTML(
                      formatMoney(
                        item.final_price,
                        cart.currency
                      )
                    )}
                    ×
                    ${item.quantity}
                  </div>

                </div>

              </div>
            `;

          })
          .join('');


      drawerBody.innerHTML = `

        <div class="drawer-items">
          ${itemsHTML}
        </div>

        <div class="drawer-total">

          <span>
            Total
          </span>

          <strong>
            ${escapeHTML(
              formatMoney(
                cart.total_price,
                cart.currency
              )
            )}
          </strong>

        </div>

        <div class="drawer-actions">

          <a
            class="btn btn--primary"
            href="/checkout"
          >
            Checkout
          </a>

          <a
            class="btn btn--ghost"
            href="/cart"
          >
            View cart
          </a>

        </div>

      `;


      return cart;

    })();


    try {

      return await cartRequest;

    } finally {

      cartRequest = null;

    }

  }


  /* =========================================================
     CART DRAWER
     ========================================================= */

  function openDrawer() {

    if (!drawer) {
      return;
    }


    drawer.setAttribute(
      'aria-hidden',
      'false'
    );


    document.body.classList.add(
      'cart-drawer-open'
    );


    renderCart();


    const firstCloseButton =
      $('[data-cart-close]', drawer);

    if (firstCloseButton) {

      setTimeout(() => {
        firstCloseButton.focus();
      }, 50);

    }

  }


  function closeDrawer() {

    if (!drawer) {
      return;
    }


    drawer.setAttribute(
      'aria-hidden',
      'true'
    );


    document.body.classList.remove(
      'cart-drawer-open'
    );

  }


  cartOpeners.forEach(element => {

    element.addEventListener(
      'click',
      event => {

        event.preventDefault();

        openDrawer();

      }
    );

  });


  cartClosers.forEach(element => {

    element.addEventListener(
      'click',
      event => {

        event.preventDefault();

        closeDrawer();

      }
    );

  });


  document.addEventListener(
    'keydown',
    event => {

      if (
        event.key === 'Escape' &&
        drawer?.getAttribute(
          'aria-hidden'
        ) === 'false'
      ) {

        closeDrawer();

      }

    }
  );


  /* =========================================================
     BUTTON STATES
     ========================================================= */

  function setLoadingState(button, loading) {

    if (!button) {
      return;
    }


    if (loading) {

      button.disabled = true;

      button.setAttribute(
        'aria-busy',
        'true'
      );


      button.dataset.originalText =
        button.textContent.trim();


      const submitText =
        button.querySelector(
          '[data-submit-text]'
        );


      const quickIcon =
        button.querySelector(
          '.quick-add__icon'
        );


      const quickText =
        button.querySelector(
          '.quick-add__text'
        );


      if (submitText) {

        submitText.textContent =
          'Adding...';

      } else if (quickText) {

        quickText.textContent =
          'Adding';

      } else if (quickIcon) {

        quickIcon.textContent =
          '…';

      } else {

        button.textContent =
          'Adding...';

      }


      return;
    }


    button.disabled = false;

    button.removeAttribute(
      'aria-busy'
    );


    const submitText =
      button.querySelector(
        '[data-submit-text]'
      );


    const quickIcon =
      button.querySelector(
        '.quick-add__icon'
      );


    const quickText =
      button.querySelector(
        '.quick-add__text'
      );


    if (submitText) {

      submitText.textContent =
        'Add to cart';

    } else if (quickText) {

      quickText.textContent =
        'Add';

    } else if (quickIcon) {

      quickIcon.textContent =
        '+';

    } else if (
      button.dataset.originalText
    ) {

      button.textContent =
        button.dataset.originalText;

    }

  }


  /* =========================================================
     AJAX ADD TO CART
     ========================================================= */

  async function addToCart(form) {

    const button =
      $('[type="submit"]', form);


    setLoadingState(
      button,
      true
    );


    try {

      const formData =
        new FormData(form);


      const cartItem =
        await fetchJSON(
          '/cart/add.js',
          {
            method: 'POST',
            body: formData,

            headers: {
              Accept:
                'application/json'
            }
          }
        );


      await renderCart();


      document.dispatchEvent(
        new CustomEvent(
          'cf:cart-updated',
          {
            detail: {
              item:
                cartItem
            }
          }
        )
      );


      openDrawer();


      return cartItem;

    } catch (error) {

      console.error(
        'Clever Finds cart error:',
        error
      );


      alert(
        error.message ||
        'We could not add this item. Please try again.'
      );


      throw error;

    } finally {

      setLoadingState(
        button,
        false
      );

    }

  }


  /* =========================================================
     ADD TO CART FORM HANDLER
     ========================================================= */

  document.addEventListener(
    'submit',
    event => {

      const form =
        event.target.closest(
          'form[action*="/cart/add"]'
        );


      if (!form) {
        return;
      }


      if (
        form.dataset.noAjax ===
        'true'
      ) {
        return;
      }


      event.preventDefault();


      if (
        form.dataset.submitting ===
        'true'
      ) {
        return;
      }


      form.dataset.submitting =
        'true';


      addToCart(form)
        .catch(() => {})
        .finally(() => {

          delete form.dataset.submitting;

        });

    }
  );


  /* =========================================================
     PRODUCT QUANTITY
     ========================================================= */

  document.addEventListener(
    'click',
    event => {

      const minus =
        event.target.closest(
          '[data-quantity-minus]'
        );


      const plus =
        event.target.closest(
          '[data-quantity-plus]'
        );


      if (!minus && !plus) {
        return;
      }


      const control =
        (minus || plus)
          .closest('.quantity-control');


      if (!control) {
        return;
      }


      const input =
        $('[data-quantity-input]', control);


      if (!input) {
        return;
      }


      let value =
        parseInt(
          input.value,
          10
        );


      if (
        !Number.isFinite(value) ||
        value < 1
      ) {
        value = 1;
      }


      if (minus) {

        value = Math.max(
          1,
          value - 1
        );

      }


      if (plus) {

        value += 1;

      }


      input.value =
        String(value);


      input.dispatchEvent(
        new Event(
          'change',
          {
            bubbles: true
          }
        )
      );

    }
  );


  /* =========================================================
     QUANTITY INPUT VALIDATION
     ========================================================= */

  document.addEventListener(
    'change',
    event => {

      const input =
        event.target.closest(
          '[data-quantity-input]'
        );


      if (!input) {
        return;
      }


      let value =
        parseInt(
          input.value,
          10
        );


      if (
        !Number.isFinite(value) ||
        value < 1
      ) {
        value = 1;
      }


      input.value =
        String(value);

    }
  );


  /* =========================================================
     INITIALIZATION
     ========================================================= */

  initNavigation();


  /* =========================================================
     SHOPIFY SECTION EVENTS
     ========================================================= */

  document.addEventListener(
    'shopify:section:load',
    event => {

      const section =
        event.target;


      if (
        section.matches?.(
          '[data-product-section]'
        ) ||
        section.querySelector?.(
          '[data-product-section]'
        )
      ) {

        /* Main product JS is initialized
           by its own section script. */

      }

    }
  );


  /* =========================================================
     INITIAL CART COUNT
     ========================================================= */

  if (
    $$('[data-cart-count]').length
  ) {

    renderCart().catch(error => {

      console.warn(
        'Unable to load cart:',
        error
      );

    });

  }

})();